import React, { useEffect, useState } from "react";
import { Typography, CircularProgress, Collapse, List, ListItem, ListItemText } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { getServerUri } from "../modules/uriUtility";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles({
  warningRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 0",
  },
  usageText: {
    fontSize: "15px",
  },
  detailsToggle: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    color: "#4eabb1",
    padding: "4px 0",
    userSelect: "none",
    fontSize: "0.9rem",
    fontWeight: 500,
    "&:hover": { textDecoration: "underline" },
  },
  gameHeader: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: "6px 12px",
    backgroundColor: "#363636",
    userSelect: "none",
    "&:hover": { backgroundColor: "#3e3e3e" },
  },
  gameTitle: {
    color: "#eee",
    fontSize: "14px",
    fontWeight: 400,
    flex: 1,
  },
  gameList: {
    maxHeight: "220px",
    overflow: "auto",
    borderRadius: "4px",
    padding: 0,
    marginTop: "4px",
  },
  versionItem: {
    padding: "1px 12px 1px 28px",
  },
  versionText: {
    color: "#ccc",
    fontSize: "0.78rem",
  },
});

export default function AssetUsageInfo({ assetID, assetFile, assetType, isOpen }) {
  const [usage, setUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedBases, setExpandedBases] = useState({});
  const classes = useStyles();

  useEffect(() => {
    if (!isOpen || !assetType) return;
    let cancelled = false;
    setIsLoading(true);
    setUsage(null);
    setIsExpanded(false);
    setExpandedBases({});

    fetch(getServerUri("version/findAssetUsage"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        accessToken: localStorage.getItem("session.accessToken"),
        assetID: assetID ? String(assetID) : undefined,
        assetFile,
        assetType,
      }),
    })
      .then((res) => res.json())
      .then((data) => { if (!cancelled) { setUsage(data); setIsLoading(false); } })
      .catch(() => { if (!cancelled) { setUsage(null); setIsLoading(false); } });

    return () => { cancelled = true; };
  }, [isOpen, assetID, assetFile, assetType]);

  const toggleBase = (id) => {
    setExpandedBases((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (isLoading) {
    return (
      <div className={classes.warningRow}>
        <CircularProgress size={20} />
        <Typography variant="body2">Checking usage across versions...</Typography>
      </div>
    );
  }

  if (!usage || usage.versionCount === 0) return null;

  return (
    <div style={{ padding: "4px 0 8px" }}>
      <div className={classes.warningRow}>
        <ErrorOutlineIcon color="secondary" />
        <Typography className={classes.usageText}>
          This asset is used in <strong>{usage.baseCount}</strong> base{usage.baseCount !== 1 ? "s" : ""},{" "}
          <strong>{usage.versionCount}</strong> version{usage.versionCount !== 1 ? "s" : ""}.
        </Typography>
      </div>

      <div className={classes.detailsToggle} onClick={() => setIsExpanded(!isExpanded)}>
        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        <Typography style={{ fontSize: "inherit", fontWeight: "inherit", color: "inherit" }}>
          {isExpanded ? "Hide" : "View"} affected bases & versions
        </Typography>
      </div>

      <Collapse in={isExpanded}>
        <List dense className={classes.gameList}>
          {usage.bases.map((base) => (
            <React.Fragment key={base._id}>
              <div className={classes.gameHeader} onClick={() => toggleBase(base._id)}>
                <Typography className={classes.gameTitle}>
                  {base.title} ({base.versions.length})
                </Typography>
                {expandedBases[base._id]
                  ? <ExpandLessIcon style={{ color: "#9bcec0" }} fontSize="small" />
                  : <ExpandMoreIcon style={{ color: "#9bcec0" }} fontSize="small" />
                }
              </div>
              <Collapse in={!!expandedBases[base._id]}>
                {base.versions.map((ver) => (
                  <ListItem key={ver._id} className={classes.versionItem}>
                    <ListItemText primary={ver.title} primaryTypographyProps={{ className: classes.versionText }} />
                  </ListItem>
                ))}
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Collapse>
    </div>
  );
}
